/**
 * Archive / Restore / Delete actions menu — per-row dropdown for admin tables.
 *
 * Props:
 *   recordType: "customer" | "trade_partner" | "property" | "booking" |
 *               "quote_request" | "proposal" | "customer_request" | "passport_test"
 *   recordId:   string
 *   recordDisplay: string  (shown in confirmation dialogs)
 *   isArchived: boolean
 *   onChanged:  () => void  (called after any action so the table refreshes)
 *   extraItems: Array<{ testId, icon, label, onClick, tone? }>  (optional —
 *               record-type-specific actions, e.g. "Disable payment
 *               reminders" on bookings, rendered above Archive/Delete.
 *               This component stays generic; callers own their own
 *               confirmation step if the action needs one.)
 */
import { useState } from "react";
import { MoreHorizontal, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";

export function ArchiveActionsMenu({ recordType, recordId, recordDisplay, isArchived, onChanged, hidden = false, extraItems = [] }) {
  const [showDelete, setShowDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  // `hidden` suppresses the Archive/Restore/Delete actions for non-admin
  // roles — those are destructive/structural. It never hides `extraItems`:
  // callers only ever pass those for non-destructive, role-appropriate
  // actions (e.g. the booking reminder toggle, which the backend already
  // permits for lab_technician too), so a non-admin who should still see
  // that one action isn't denied a menu entirely just because they can't
  // archive/delete.
  if (hidden && extraItems.length === 0) return null;

  const doArchive = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/archive/${recordType}/${recordId}`, { reason: null });
      toast.success(`Archived ${recordDisplay}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doRestore = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/restore/${recordType}/${recordId}`);
      toast.success(`Restored ${recordDisplay}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            data-testid={`row-actions-${recordType}-${recordId}`}
            className="h-8 w-8 p-0"
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {extraItems.length > 0 && (
            <>
              {extraItems.map((item) => (
                <DropdownMenuItem
                  key={item.testId}
                  data-testid={item.testId}
                  onClick={item.onClick}
                  className={item.tone === "warning" ? "text-amber-700 focus:text-amber-800" : undefined}
                >
                  {item.icon ? <item.icon className="w-4 h-4 mr-2" /> : null}
                  {item.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
          {!hidden && (isArchived ? (
            <DropdownMenuItem
              data-testid={`row-restore-${recordType}-${recordId}`}
              onClick={doRestore}
            >
              <ArchiveRestore className="w-4 h-4 mr-2 text-emerald-600" />
              Restore
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              data-testid={`row-archive-${recordType}-${recordId}`}
              onClick={doArchive}
            >
              <Archive className="w-4 h-4 mr-2 text-amber-600" />
              Archive
            </DropdownMenuItem>
          ))}
          {!hidden && <DropdownMenuSeparator />}
          {!hidden && (
            <DropdownMenuItem
              data-testid={`row-delete-${recordType}-${recordId}`}
              onClick={() => setShowDelete(true)}
              className="text-rose-600 focus:text-rose-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete…
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
       </DropdownMenu>
      <ConfirmDeleteModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        recordType={recordType}
        recordId={recordId}
        recordDisplay={recordDisplay}
        onDeleted={() => {
          setShowDelete(false);
          onChanged?.();
        }}
      />
    </>
  );
}

export default ArchiveActionsMenu;
